import { browser } from '$app/env'
import { SvelteDate } from 'svelte/reactivity'
import type { Table, TypedSupabaseClient } from '$lib/supabase'
import type { Movement } from '$lib/types'
import { getBrowserTimezone } from '$lib/utils/timezone'
import { getContext, setContext } from 'svelte'
import { derived, get, writable } from 'svelte/store'
import { addCurrent } from './add-current.svelte'
import { readStorage, writeStorage } from './storable'

type StoredMovement = {
  date: string
  movement: Movement
}

const _KEY = {}

const setModContext = (value: MovementOfTheDay) => setContext(_KEY, value)

type ModDepenencies = {
  supabase: TypedSupabaseClient
  timezone: string | undefined
}

export const createModContext = (dependencies: ModDepenencies) =>
  setModContext(new MovementOfTheDay(dependencies))

export const getModContext = () => getContext<MovementOfTheDay>(_KEY)

const padNum = (num: number) => `${num}`.padStart(2, '0')

const getDateString = (date: Date) =>
  `${date.getFullYear()}-${padNum(date.getMonth() + 1)}-${padNum(date.getDate())}`

export class MovementOfTheDay {
  #dependencies: ModDepenencies
  #key = 'movementOfTheDay'

  #store = addCurrent(
    writable<StoredMovement | undefined>(undefined, () => {
      if (!browser) return
      this.#updateMovementOfTheDay()
      const interval = setInterval(() => {
        this.#updateMovementOfTheDay()
      }, 10000)
      return () => clearInterval(interval)
    }),
  )

  constructor(dependencies: ModDepenencies) {
    this.#dependencies = dependencies
  }

  get store() {
    return derived(
      this.#store,
      ($movementOfTheDay) => $movementOfTheDay?.movement,
    )
  }

  get current() {
    return this.#store.current?.movement
  }

  load = async (): Promise<Movement | undefined> =>
    (await this.#updateMovementOfTheDay())?.movement

  #loadMovement = async (): Promise<Table<'movements'> | undefined> => {
    const supabase = this.#dependencies.supabase
    const timezone =
      this.#dependencies.timezone ?? (await getBrowserTimezone(supabase))

    const { data, error } = await supabase.rpc('get_mod', {
      _timezone: timezone,
    })

    const movement = data?.[0]

    if (error || !movement) {
      console.error(error?.message ?? 'Unable to load movement of the day')
      return undefined
    }

    return movement
  }

  async #updateMovementOfTheDay(): Promise<StoredMovement | undefined> {
    const dayStartString = getDateString(new SvelteDate())

    const storedValue = get(this.#store)
    if (storedValue && storedValue.date === dayStartString) return storedValue

    let storedMovement: StoredMovement | undefined
    const storedJson = readStorage(this.#key)
    if (storedJson) {
      try {
        storedMovement = JSON.parse(storedJson) as StoredMovement
        if (storedMovement.date !== dayStartString) {
          storedMovement = undefined
        }
      } catch (_) {
        // Nothing to do.
      }
    }

    // The movement of the day could be loaded from local storage, so let's
    // escape here.
    if (storedMovement) {
      this.#store.set(storedMovement)
      return storedMovement
    }

    console.log(
      `Didn't find a stored movement of the day, so loading it from database.`,
    )
    const movement = await this.#loadMovement()

    if (movement) {
      storedMovement = {
        date: dayStartString,
        movement: movement as Movement,
      }
      writeStorage(this.#key, JSON.stringify(storedMovement))
    }

    this.#store.set(storedMovement)
    return storedMovement
  }
}
